/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ApiKeysColumns.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom column renderers for API Keys DataGrid.
────────────────────────────────────────────────────────────────*/

import { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { ApiKey } from '../types';

export function useApiKeysColumnRenderers() {
  const { t, i18n } = useI18n();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === 'es' ? 'es-ES' : 'en-US',
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
    );
  };

  const columnRenderers = useMemo(
    () => ({
      name: (value: string) => (
        <div>
          <div className="font-medium text-foreground">{value}</div>
        </div>
      ),
      description: (value: string) => (
        <div className="text-muted-foreground">{value || '-'}</div>
      ),
      keyPrefix: (value: string) => (
        <div className="font-mono text-sm text-foreground">{value}</div>
      ),
      tenant: (value: string | { name: string; slug: string } | null) => {
        if (!value) {
          return <div className="text-muted-foreground">{t('apiKeys.global')}</div>;
        }
        if (typeof value === 'string') {
          return <div className="text-foreground">{value}</div>;
        }
        return (
          <div className="text-foreground">
            {value.name} ({value.slug})
          </div>
        );
      },
      isActive: (value: boolean | string) => {
        const isActive = typeof value === 'boolean' ? value : value === t('common.yes');
        return (
          <div className={isActive ? 'text-green-600' : 'text-red-600'}>
            {isActive ? t('common.yes') : t('common.no')}
          </div>
        );
      },
      lastUsedAt: (value: string) => (
        <div className="text-foreground">
          {value ? formatDate(value) : t('apiKeys.never')}
        </div>
      ),
      expiresAt: (value: string) => (
        <div className="text-foreground">
          {value ? formatDate(value) : t('apiKeys.never')}
        </div>
      ),
      creator: (value: string | { username: string; fullName?: string }) => {
        if (typeof value === 'string') {
          return <div className="text-foreground">{value}</div>;
        }
        return (
          <div className="text-foreground">{value.fullName || value.username}</div>
        );
      },
      created_at: (value: string) => (
        <div className="text-foreground">{formatDate(value)}</div>
      ),
    }),
    [t, i18n.language],
  );

  return columnRenderers;
}

